'use client'

import { useEffect, useRef, useState } from 'react'
import { Play, Store } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/core/components/avatar'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/core/components/carousel'
import { resolveShopImageUrl } from '@/features/shop/lib/shop-image'
import { shopClipTitle } from '@/features/shop/models/shop-clip.model'
import type { ShopClip } from '@/features/shop/types/shop.types'
import { ClipViewerDialog } from './clip-viewer-dialog.client'

/** A clip card's cover: the signed poster, else the product photo, else a play glyph. */
function ClipPoster({ clip }: { clip: ShopClip }) {
  const sources = [clip.posterUrl, resolveShopImageUrl(clip.product?.images[0])].filter(
    (source, index, all): source is string => Boolean(source) && all.indexOf(source) === index
  )
  const signature = sources.join('|')
  const [sourceIndex, setSourceIndex] = useState(0)
  useEffect(() => {
    setSourceIndex(0)
  }, [signature])

  const source = sources[sourceIndex]
  if (!source) {
    return (
      <div className="flex h-full items-center justify-center">
        <Play className="h-9 w-9 text-muted-foreground" />
      </div>
    )
  }
  return (
    // Signed Shop storage URLs — a host the optimizer allowlist can't cover.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={source}
      alt=""
      loading="lazy"
      onError={() => setSourceIndex((current) => current + 1)}
      className="h-full w-full object-cover transition-transform duration-300 motion-reduce:transition-none group-hover:scale-[1.02]"
    />
  )
}

export function LandingClipsRow({ clips }: { clips: ShopClip[] }) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)

  const open = (index: number, target: HTMLElement) => {
    openerRef.current = target
    setActiveIndex(index)
  }
  const close = () => {
    setActiveIndex(null)
    window.requestAnimationFrame(() => openerRef.current?.focus())
  }

  return (
    <>
      <Carousel opts={{ align: 'start', slidesToScroll: 1 }} className="mt-8" aria-label="Shop clips">
        <CarouselContent className="-ml-3">
          {clips.map((clip, index) => (
            <CarouselItem key={clip.id} className="basis-[55%] pl-3 sm:basis-[32%] md:basis-[24%] lg:basis-1/5">
              <button
                type="button"
                onClick={(event) => open(index, event.currentTarget)}
                className="group block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label={`Play ${shopClipTitle(clip)}`}
              >
                <div className="relative aspect-[9/16] overflow-hidden rounded-md border border-border bg-secondary">
                  <ClipPoster clip={clip} />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background/90 shadow-sm">
                      <Play className="ml-0.5 h-5 w-5 text-primary" fill="currentColor" />
                    </span>
                  </span>
                </div>
                <p className="mt-3 line-clamp-2 text-sm font-semibold leading-5 text-navy">{shopClipTitle(clip)}</p>
                {clip.business && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={clip.business.logoUrl ?? ''} />
                      <AvatarFallback>
                        <Store className="h-3 w-3" />
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate">{clip.business.name}</span>
                  </div>
                )}
              </button>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-2 top-[42%] hidden bg-background/95 sm:flex" />
        <CarouselNext className="right-2 top-[42%] hidden bg-background/95 sm:flex" />
      </Carousel>
      <ClipViewerDialog
        clips={clips}
        activeIndex={activeIndex}
        onOpenChange={(openState) => {
          if (!openState) close()
        }}
        onChange={setActiveIndex}
      />
    </>
  )
}
